import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import apiService from '../../services/apiService';
import { Button } from '../ui';
import { NEWS_STEPS, ONBOARDING_VERSION, PAGE_TIPS, sessionSteps } from './onboardingContent';
import OnboardingTour from './OnboardingTour';
import styles from './Onboarding.module.css';

const OnboardingContext = createContext({ startTour: () => { }, resetTips: () => { }, dismissTip: () => { }, isTipSeen: () => true, ready: false });

// Lokale Kopie pro Benutzer: greift, solange der Server die Einstellungen (noch) nicht kennt oder offline ist
const localKey = (userId) => `btmx.onboarding.${userId}`;
function readLocal(userId) {
  try { return JSON.parse(localStorage.getItem(localKey(userId))) || null; } catch { return null; }
}
function writeLocal(userId, value) {
  try { localStorage.setItem(localKey(userId), JSON.stringify(value)); } catch { /* Speicher gesperrt */ }
}

/**
 * Einführung für neue (und auf Wunsch bestehende) Nutzer.
 * - Rundgang startet automatisch beim ersten Anmelden (nur im neuen Design und wenn kein anderer Dialog offen ist)
 * - Neue Hinweise (NEWS_STEPS) erscheinen einmal nach dem Anmelden – bei neuen Nutzern im Rundgang, sonst allein
 * - Fortschritt wird pro Benutzer auf dem Server gespeichert (user.preferences.onboarding)
 */
export function OnboardingProvider({ user, navigation, initialOnboarding, autoStart, blocked, children }) {
  const userId = user?.userID ?? user?.id ?? user?.username;
  const [state, setState] = useState(null);
  // null = geschlossen, 'auto' = nach dem Anmelden, 'manual' = über Einstellungen/Profilmenü
  const [tourMode, setTourMode] = useState(null);
  const tourOpen = !!tourMode;
  const autoStarted = useRef(false);

  // Server-Stand (falls vorhanden) gewinnt, sonst lokale Kopie
  useEffect(() => {
    if (!userId || initialOnboarding === undefined) return;
    const next = initialOnboarding || readLocal(userId) || {};
    setState({ version: 0, done: false, seenTips: [], ...next });
  }, [userId, initialOnboarding]);

  const persist = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      if (userId) writeLocal(userId, next);
      return next;
    });
    apiService.put('/user/preferences', { onboarding: patch }).catch(() => { /* lokal gespeichert */ });
  }, [userId]);

  useEffect(() => {
    if (!state || autoStarted.current || !autoStart || blocked) return;
    autoStarted.current = true;
    if (sessionSteps(navigation || [], state).length > 0) setTourMode('auto');
  }, [state, autoStart, blocked, navigation]);

  const steps = useMemo(
    () => (tourMode ? sessionSteps(navigation || [], state, { manual: tourMode === 'manual' }) : []),
    [tourMode, navigation, state]
  );

  // Gezeigte Hinweise merken; der Rundgang gilt als gesehen, wenn er Teil des Fensters war
  const finishTour = useCallback(() => {
    if (!tourMode) return;
    const shownNews = steps.filter((step) => NEWS_STEPS.some((news) => news.id === step.id)).map((step) => step.id);
    const tourShown = steps.some((step) => step.id === 'done');
    setTourMode(null);
    persist({
      ...(tourShown && { version: ONBOARDING_VERSION, done: true }),
      seenNews: [...new Set([...(state?.seenNews || []), ...shownNews])]
    });
  }, [tourMode, steps, state, persist]);

  const value = useMemo(() => ({
    ready: !!state && !tourOpen,
    startTour: () => setTourMode('manual'),
    isTipSeen: (id) => !state || state.seenTips?.includes(id),
    dismissTip: (id) => persist({ seenTips: [...new Set([...(state?.seenTips || []), id])] }),
    resetTips: () => persist({ seenTips: [] })
  }), [state, tourOpen, persist]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <OnboardingTour open={tourOpen} steps={steps} onFinish={finishTour} />
    </OnboardingContext.Provider>
  );
}

export const useOnboarding = () => useContext(OnboardingContext);

/** Kurzer Tipp beim ersten Besuch einer Seite; verschwindet dauerhaft mit „Verstanden“ */
export function PageTip({ pathname, icon: Icon }) {
  const { ready, isTipSeen, dismissTip } = useOnboarding();
  const tip = PAGE_TIPS[pathname];
  if (!ready || !tip || isTipSeen(tip.id)) return null;
  return (
    <aside className={styles.tip} aria-label="Tipp">
      {Icon && <Icon className={styles.tipIcon} aria-hidden />}
      <p className={styles.tipText}>{tip.text}</p>
      <Button size="sm" variant="ghost" icon={X} onClick={() => dismissTip(tip.id)}>Verstanden</Button>
    </aside>
  );
}
